/** @jsxImportSource @emotion/react */
import { Collapsible } from 'grommet'
import { useTranslation } from 'next-i18next'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { useInsert, UseInsertState } from 'react-supabase'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuth } from 'hooks/useAuth'
import { useProvider } from 'hooks/useMoodPlot'
import { ExistingGroup, Mood } from 'db-tables'
import Notification from 'components/Notification'
import { EventType, trackEvent } from 'utils/useGTM'
import PlotAsBox from 'components/group/plot/PlotAsBox'
import DisplayNameBox from 'components/group/plot/DisplayNameBox'

import SplitMoodButton from './SplitMoodButton'

type MoodResp = Pick<UseInsertState<Mood>, 'count' | 'data' | 'error'>
type AddMoodProps = {
  group: ExistingGroup
  onCreate?: (resp: MoodResp) => void
}

export default function AddMoodBox({ group, onCreate }: AddMoodProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [isOpened, setIsOpened] = useState(false)
  const { state, resetWords, resetAnonymity } = useProvider()
  const [{ error, fetching }, execInsert] = useInsert<Mood>('moods')

  const onSuccess = useCallback(
    (resp: MoodResp) => {
      resetWords()
      resetAnonymity()
      onCreate && onCreate(resp)
    },
    [onCreate, resetAnonymity, resetWords],
  )

  const prepareMoods = useCallback(
    async (visitorId: string) => {
      return state.moods?.map((mood) => ({
        angle: mood.angle,
        emotion_id: mood.emotion_id,
        group_id: group.id,
        user_id: user?.id,
        session_id: visitorId,
        anon: state.anon,
        anon_user_name: state.displayName ?? null,
        journal: state.journalContent,
      }))
    },
    [group.id, state.anon, state.displayName, state.journalContent, state.moods, user?.id],
  )
  const plotEvent = useMemo<EventType>(() => {
    if (state?.anon) return 'PlotAsAnonymous'
    if (state?.displayName) return 'PlotAsDisplayName'
    return 'PlotAsUser'
  }, [state?.anon, state?.displayName])

  const insertMood = useCallback(async () => {
    const agent = await FingerprintJS.load()
    const fingerprint = await agent.get()
    const prepared = await prepareMoods(fingerprint.visitorId)
    if (!prepared) return
    const resp = await execInsert(prepared)
    if (!resp.error) {
      trackEvent({ event: 'SubmitPlot' })
      trackEvent({ event: plotEvent })
      onSuccess(resp)
    } else {
      trackEvent({ event: 'ErrorSubmitPlot' })
    }
  }, [prepareMoods, execInsert, plotEvent, onSuccess])

  const disabled = useMemo(() => {
    return (
      fetching || !state.angle || state.moods.length === 0 || (!user && group.allow_anon === false)
    )
  }, [fetching, state.angle, state.moods.length, user, group.allow_anon])

  // Auto close plot as box
  useEffect(() => {
    if (disabled) setIsOpened(false)
  }, [disabled])

  // Auto open plot as box when user has chosen at least one mood
  useEffect(() => {
    if (state.moods.length > 0) setIsOpened(true)
  }, [state.moods.length])

  const toggleOpen = useCallback(
    () =>
      setIsOpened((open) => {
        trackEvent({ event: open ? 'PlotAsPanelClose' : 'PlotAsPanelOpen' })
        return !open
      }),
    [],
  )

  return (
    <div>
      {error && (
        <Notification fill="horizontal" margin={{ bottom: 'small' }} status="error">
          {error?.message}
        </Notification>
      )}
      {!user && group.allow_anon === false && (
        <Notification fill="horizontal" margin={{ bottom: 'small' }} status="warning">
          {t('components.add-mood-button.no-anonymous')}
        </Notification>
      )}

      <SplitMoodButton
        open={isOpened}
        onToggle={toggleOpen}
        disabled={disabled}
        fetching={fetching}
        onClick={insertMood}
      />
      <PlotAsBox />
      <Collapsible open={isOpened}>
        <DisplayNameBox allowAnonymous={group.allow_anon} />
      </Collapsible>
    </div>
  )
}
